import { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, type Swiper as SwiperType } from 'swiper';
import 'swiper/swiper-bundle.css';
import 'swiper/css';
import { Attraction, Product } from '../../types/pages/tour';
import AttractionModal from '../TourPage/AttractionPopUp';
import React from 'react';
import {
  LeftSwipperArrows,
  RightSwipperArrows
} from '../../shared-components/SwipperArrows/SwipperArrows';

interface WhatYouWillSeeSectionProps {
  product: Product;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
}

export const WhatYouWillSeeSection = ({
  product,
  showModal,
  setShowModal
}: WhatYouWillSeeSectionProps) => {
  const [isLastSlide, setIsLastSlide] = useState(false);
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const prevRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);
  const [selectedAttraction, setSelectedAttraction] =
    useState<Attraction | null>(null);

  const sortedAttractions = React.useMemo(() => {
    if (!product?.attractions) return [];

    return [...product.attractions].sort((a, b) => {
      const hasCardMessageA = a.card_message && a.card_message.trim() !== '';
      const hasCardMessageB = b.card_message && b.card_message.trim() !== '';

      if (hasCardMessageA === hasCardMessageB) return 0;

      return hasCardMessageA ? -1 : 1;
    });
  }, [product?.attractions]);

  useEffect(() => {
    setIsFirstSlide(true);
    setIsLastSlide(false);

    if (swiperInstance && typeof swiperInstance.slideTo === 'function') {
      setTimeout(() => {
        try {
          swiperInstance.slideTo(0, 0);
        } catch (error) {
          console.warn('Failed to reset slider position:', error);
        }
      }, 0);
    }
  }, [product]);

  const onBeforeInit = (Swiper: SwiperType): void => {
    if (typeof Swiper.params.navigation !== 'boolean') {
      const navigation = Swiper.params.navigation;
      if (navigation) {
        navigation.prevEl = prevRef.current;
        navigation.nextEl = nextRef.current;
      }
    }
  };

  const handleSlideChange = (swiper: SwiperType) => {
    setIsFirstSlide(swiper.isBeginning);
    setIsLastSlide(swiper.isEnd);
  };

  const openModal = (attraction: Attraction) => {
    setSelectedAttraction(attraction);
    setShowModal(true);
  };

  return (
    <div className="flex flex-col justify-between w-full max-w-[100vw]">
      <div className="flex pb-4 justify-between items-center">
        <h2
          className="text-[22px] md:text-[24px] lg:text-[26px] font-semibold text-start"
          id="what-you-will-see"
        >
          What you&apos;ll see
        </h2>
        <div className="md:flex items-center gap-2 hidden">
          {sortedAttractions?.length > 1 && (
            <div id="slider-btn" className="flex gap-2 items-center">
              <div ref={prevRef}>
                <LeftSwipperArrows isFirstSlide={isFirstSlide} />
              </div>
              <div ref={nextRef}>
                <RightSwipperArrows isLastSlide={isLastSlide} />
              </div>
            </div>
          )}
        </div>
      </div>

      <Swiper
        modules={[Navigation]}
        className="relative w-full"
        spaceBetween={20}
        slidesPerView={1}
        onSlideChange={handleSlideChange}
        onBeforeInit={onBeforeInit}
        onSwiper={setSwiperInstance}
        navigation={{
          prevEl: prevRef?.current,
          nextEl: nextRef?.current
        }}
        grabCursor
        breakpoints={{
          300: { slidesPerView: 1.5 },
          600: { slidesPerView: 2 },
          800: { slidesPerView: 1.8 },
          1024: { slidesPerView: 2 },
          1200: { slidesPerView: 2.5 },
          1600: { slidesPerView: 3 }
        }}
      >
        {sortedAttractions.length > 0 ? (
          sortedAttractions.map((attraction: Attraction) => {
            const { alt, url } = attraction?.heroMedia[0] || {};
            const hasCardMessage =
              attraction.card_message && attraction.card_message.trim() !== '';

            return (
              <SwiperSlide
                key={attraction.name}
                onClick={() => openModal(attraction)}
              >
                <div className="bg-white shadow-sm overflow-hidden h-[295px] md:h-[280px] max-h-[295px] md:max-h-[285px] cursor-pointer">
                  <div className="relative w-full h-[160px]">
                    <Image
                      src={url}
                      alt={alt || `${attraction.name} in 2024`}
                      fill
                      style={{ objectFit: 'cover' }}
                      className="rounded-lg"
                      loading="lazy"
                    />
                    {hasCardMessage && (
                      <div className="absolute top-0 left-0 bg-primary text-white px-2 py-1 rounded text-xs font-medium z-10">
                        {attraction.card_message}
                      </div>
                    )}
                  </div>
                  <div className="py-4">
                    <h3 className="text-base sm:text-[17px] font-semibold text-[#101010] mb-2 whitespace-nowrap overflow-hidden text-ellipsis">
                      {attraction.name}
                    </h3>
                    <p className="text-sm md:text-[15px] text-[#677083]">
                      {attraction?.cardSnippet?.length > 100
                        ? `${attraction.cardSnippet.slice(0, 100)}...`
                        : attraction.cardSnippet}
                    </p>
                  </div>
                </div>
              </SwiperSlide>
            );
          })
        ) : (
          <div className="flex justify-start items-center h-12 text-[14px] md:text-base">
            No Attractions to show
          </div>
        )}
      </Swiper>

      {/* Display modal when showModal is true */}
      {showModal && selectedAttraction && (
        <AttractionModal
          attraction={selectedAttraction}
          setShowModal={setShowModal}
        />
      )}
    </div>
  );
};

export default WhatYouWillSeeSection;
